/**
 * API Client für Content Blocks
 * 
 * Endpoints für Content Block CRUD Operationen.
 */

import apiClient from '../api-client'
import type {
  ContentBlocksResponse,
  ContentBlockResponse,
  ContentBlockCreateResponse,
  ContentBlockUpdateResponse,
  CreateContentBlockRequest,
  UpdateContentBlockRequest,
  ContentBlocksQuery,
} from '@/types/content-blocks'

const BASE_URL = '/api/v1/content-blocks'

/**
 * Get all content blocks (with optional filters)
 */
export async function getContentBlocks(
  query?: ContentBlocksQuery
): Promise<ContentBlocksResponse> {
  const params = new URLSearchParams()
  
  if (query?.scope_type) params.append('scope_type', query.scope_type)
  if (query?.scope_key) params.append('scope_key', query.scope_key)
  if (query?.block_type) params.append('block_type', query.block_type)
  
  const queryString = params.toString()
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL
  
  const response = await apiClient.get<ContentBlocksResponse>(url)
  return response.data
}

/**
 * Get content blocks for a specific product
 */
export async function getProductContentBlocks(
  productId: string
): Promise<ContentBlocksResponse> {
  return getContentBlocks({
    scope_type: 'product',
    scope_key: productId,
  })
}

/**
 * Get content blocks for a specific scope (product, channel_package, global_preset)
 */
export async function getContentBlocksForScope(
  scopeType: 'product' | 'channel_package' | 'global_preset',
  scopeKey: string
): Promise<ContentBlocksResponse> {
  return getContentBlocks({
    scope_type: scopeType,
    scope_key: scopeKey,
  })
}

/**
 * Get a single content block with versions
 */
export async function getContentBlock(
  blockId: string
): Promise<ContentBlockResponse> {
  const response = await apiClient.get<ContentBlockResponse>(
    `${BASE_URL}/${blockId}`
  )
  return response.data
}

/**
 * Create a new content block
 */
export async function createContentBlock(
  data: CreateContentBlockRequest
): Promise<ContentBlockCreateResponse> {
  const response = await apiClient.post<ContentBlockCreateResponse>(
    BASE_URL,
    data
  )
  return response.data
}

/**
 * Update a content block (creates new version)
 */
export async function updateContentBlock(
  blockId: string,
  data: UpdateContentBlockRequest
): Promise<ContentBlockUpdateResponse> {
  const response = await apiClient.put<ContentBlockUpdateResponse>(
    `${BASE_URL}/${blockId}`,
    data
  )
  return response.data
}

/**
 * Delete a content block
 */
export async function deleteContentBlock(
  blockId: string
): Promise<{ success: boolean; error?: string }> {
  const response = await apiClient.delete<{ success: boolean; error?: string }>(
    `${BASE_URL}/${blockId}`
  )
  return response.data
}


